import { Injectable } from '@angular/core';

export interface FileValidationConfig {
  maxSizeBytes: number;
  allowedTypes: string[];
  maxFiles?: number;
}

export interface FileValidationResult {
  isValid: boolean;
  errors: string[];
}

@Injectable({
  providedIn: 'root'
})
export class FileValidationService {
  
  // Standard file size limits (in bytes)
  public static readonly FILE_SIZE_LIMITS = {
    SMALL: 1 * 1024 * 1024,      // 1MB
    MEDIUM: 5 * 1024 * 1024,     // 5MB
    LARGE: 10 * 1024 * 1024,     // 10MB
    EXTRA_LARGE: 50 * 1024 * 1024 // 50MB
  };

  // Standard file type configurations
  public static readonly FILE_TYPE_CONFIGS = {
    IMAGES: {
      maxSizeBytes: FileValidationService.FILE_SIZE_LIMITS.MEDIUM,
      allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
      maxFiles: 1
    },
    DOCUMENTS: {
      maxSizeBytes: FileValidationService.FILE_SIZE_LIMITS.LARGE,
      allowedTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
      maxFiles: 5
    },
    CONTRACTS: {
      maxSizeBytes: FileValidationService.FILE_SIZE_LIMITS.LARGE,
      allowedTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'],
      maxFiles: 10
    },
    USER_PROFILE: {
      maxSizeBytes: FileValidationService.FILE_SIZE_LIMITS.SMALL,
      allowedTypes: ['image/jpeg', 'image/jpg', 'image/png'],
      maxFiles: 1
    },
    INTERACTION_DOCS: {
      maxSizeBytes: FileValidationService.FILE_SIZE_LIMITS.LARGE,
      allowedTypes: ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
      maxFiles: 1
    }
  };

  /**
   * Validate a single file against the provided configuration
   */
  validateFile(file: File, config: FileValidationConfig): FileValidationResult {
    const errors: string[] = [];

    // Check file size
    if (file.size > config.maxSizeBytes) {
      const maxSizeMB = config.maxSizeBytes / (1024 * 1024);
      errors.push(`File size must be less than ${maxSizeMB}MB`);
    }

    // Check file type
    if (!config.allowedTypes.includes(file.type)) {
      const allowedExtensions = config.allowedTypes.map(type => this.getFileExtension(type)).join(', ');
      errors.push(`File type not allowed. Allowed types: ${allowedExtensions}`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate multiple files against the provided configuration
   */
  validateFiles(files: File[], config: FileValidationConfig): FileValidationResult {
    const errors: string[] = [];

    // Check number of files
    if (config.maxFiles && files.length > config.maxFiles) {
      errors.push(`Maximum ${config.maxFiles} files allowed`);
    }

    // Validate each file
    files.forEach((file, index) => {
      const fileResult = this.validateFile(file, config);
      if (!fileResult.isValid) {
        errors.push(`File ${index + 1} (${file.name}): ${fileResult.errors.join(', ')}`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get file extension from MIME type
   */
  private getFileExtension(mimeType: string): string {
    const extensions: { [key: string]: string } = {
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'application/pdf': '.pdf',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
      'application/vnd.ms-excel': '.xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx'
    };
    return extensions[mimeType] || mimeType;
  }

  /**
   * Format file size in human readable format
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get configuration for specific upload type
   */
  getConfigForType(type: string): FileValidationConfig {
    switch (type.toLowerCase()) {
      case 'user_image_upload':
        // ID card uploads: allow PDF and images, 10MB limit
        return {
          maxSizeBytes: FileValidationService.FILE_SIZE_LIMITS.LARGE,
          allowedTypes: ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
          maxFiles: 1
        };
      case 'profile':
        return FileValidationService.FILE_TYPE_CONFIGS.USER_PROFILE;
      case 'company_logo_upload':
        return FileValidationService.FILE_TYPE_CONFIGS.USER_PROFILE; // Same as user profile - images only, 1MB limit
      case 'terms_conditions_upload':
      case 'privacy_policy_upload':
        return {
          maxSizeBytes: FileValidationService.FILE_SIZE_LIMITS.LARGE,
          allowedTypes: ['application/pdf'],
          maxFiles: 1
        };
      case 'contract':
        return FileValidationService.FILE_TYPE_CONFIGS.CONTRACTS;
      case 'interaction':
      case 'document':
        return FileValidationService.FILE_TYPE_CONFIGS.INTERACTION_DOCS;
      case 'image':
        return FileValidationService.FILE_TYPE_CONFIGS.IMAGES;
      default:
        return FileValidationService.FILE_TYPE_CONFIGS.DOCUMENTS;
    }
  }
}
